import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import delay from '../../../common/utils/lib/delay';
import { useDispatch, useSelector } from 'react-redux';

// Components
import useManageElementSelectionChanges from './hooks/useInboxManageElementSelectionChanges';
import useSheet from '../structural/sheet/hooks/useSheet';
import useScaledMobileScrollableList from '../../canvas/zoom/list/useScaledMobileScrollableList';
import useInboxManageAutomaticOpen from './hooks/useInboxManageAutomaticOpen';

import SheetContainer, { CloseButton, Content, Header, Heading, NonDraggableArea, Sheet } from '../structural/sheet';
import InboxClosed from '../../inbox/InboxClosed';
import PoiBoardSectionContext from '../../components/pointsOfInterest/PoiBoardSectionContext';
import InboxScrollableList from '../../inbox/InboxScrollableList';
import MobileCanvasContextMenu from '../contextMenu/MobileCanvasContextMenu';
import { ZoomStateContextProvider } from '../../canvas/zoom/ZoomStateContext';
import ListPlaceholderContextProvider from '../../element/elementPlaceholder/ListPlaceholderContextProvider';

// Actions
import { finishEditingCurrentElement } from '../../element/selection/selectionActions';
import { setFocusSection } from '../../reducers/focusActions';

// Hooks
import useInboxReturnState from '../../inbox/returnState/useInboxReturnState';

// Utils
import { getIsSheetOpen } from '../structural/sheet/sheetSelectors';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { BoardSections } from '../../../common/boards/boardConstants';
import { SheetId } from '../structural/sheet/sheetTypes';
import { PlatformDetails } from '../../../common/platform/platformTypes';
import { WORKSPACE_SECTIONS } from '../../workspace/workspaceConstants';

import './MobileInbox.scss';

const STATE_SWITCH_DELAY_MS = 100;
const STATE_SWITCH_DELAY_MS_CLOSING = 140;

type MobileInboxContainerProps = {
    currentBoard: ImMNElement;
    currentBoardId: string;
    gridSize: number;
    inboxElementIds: string[];
    isEditable: boolean;
    createShortcutFile: () => void;
    deselectAll: () => void;
    platformDetails: PlatformDetails;
};

type MobileInboxContainerContentProps = MobileInboxContainerProps & {
    listContainerRef: React.RefObject<HTMLDivElement>;
    cleanupOnOrchestrationEnd: () => void;
};

const MobileInboxContainerContent = (props: MobileInboxContainerContentProps) => {
    const { platformDetails, gridSize } = props;

    const dispatch = useDispatch();

    const { listZoomScale } = useScaledMobileScrollableList(platformDetails, gridSize);

    const inboxListRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        dispatch(setFocusSection(WORKSPACE_SECTIONS.INBOX));

        return () => {
            dispatch(setFocusSection(WORKSPACE_SECTIONS.CANVAS));
        };
    });

    return (
        <NonDraggableArea>
            <ZoomStateContextProvider zoomScale={listZoomScale}>
                <div className="inbox-scrollable-list-container" ref={inboxListRef}>
                    <InboxScrollableList
                        {...props}
                        // @ts-ignore-next-line The props are correct, just confused by untyped decorators
                        defaultFirstBatchSize={5}
                        listZoomScale={listZoomScale}
                    />
                </div>
            </ZoomStateContextProvider>
            <MobileCanvasContextMenu workspaceRef={inboxListRef} />
        </NonDraggableArea>
    );
};

const MobileInboxContainer = (props: MobileInboxContainerProps) => {
    const {
        inboxElementIds,
        currentBoardId,
        currentBoard,
        isEditable,

        deselectAll,
        createShortcutFile,
    } = props;

    const sheetProps = useSheet(SheetId.Inbox);
    const {
        isSheetOpen,
        activeSnapPoint: currentActiveSnapPoint,
        dispatchOpenSheet,
        dispatchCloseSheet,
        dispatchUpdateActiveSnapPoint,
    } = sheetProps;

    const [isClosedButtonVisible, setIsClosedButtonVisible] = useState(true);
    const isTemplatePickerSheetOpen = useSelector(getIsSheetOpen(SheetId.TemplatePicker));

    const listContainerRef = useRef(null);
    const sheetOpenAndHeightSet = isSheetOpen && currentActiveSnapPoint !== 0;
    const { cleanupOnOrchestrationEnd, saveInboxReturnStateToLocalStorage } = useInboxReturnState(
        sheetOpenAndHeightSet,
        currentBoardId,
        listContainerRef,
    );

    const dispatch = useDispatch();
    const dispatchFinishEditingElement = () => dispatch(finishEditingCurrentElement());

    const revealClosedButton = async () => {
        await delay(STATE_SWITCH_DELAY_MS_CLOSING);
        setIsClosedButtonVisible(true);
    };

    const openInboxSheet = async () => {
        setIsClosedButtonVisible(false);

        await delay(STATE_SWITCH_DELAY_MS);

        dispatchOpenSheet();
    };

    const onCloseTransitionStart = () => {
        saveInboxReturnStateToLocalStorage();
        revealClosedButton();
    };

    const updateActiveSnapPoint = useCallback(
        (activeSnapPoint: number) => {
            if (activeSnapPoint === currentActiveSnapPoint) return;

            // If we're moving in between snap points, finish editing the current element to hide keyboard
            if (currentActiveSnapPoint !== 0) {
                dispatchFinishEditingElement();
            }

            if (activeSnapPoint) {
                dispatchUpdateActiveSnapPoint(activeSnapPoint);
            }
        },
        [currentActiveSnapPoint],
    );

    useEffect(() => {
        setIsClosedButtonVisible(!isTemplatePickerSheetOpen);
    }, [isTemplatePickerSheetOpen]);

    useInboxManageAutomaticOpen(currentBoard, inboxElementIds, isSheetOpen, openInboxSheet);
    useManageElementSelectionChanges(currentBoardId, isSheetOpen, deselectAll, dispatchCloseSheet);

    return (
        <ListPlaceholderContextProvider key={currentBoardId} retainPreviousVisibleElements>
            <InboxClosed
                className={classNames({
                    transitioned: true,
                    hidden: !isClosedButtonVisible,
                })}
                openInboxHandler={openInboxSheet}
                createShortcutFile={createShortcutFile}
                isEditable={isEditable}
                inboxElementIds={inboxElementIds}
                element={currentBoard}
            />

            <SheetContainer
                {...sheetProps}
                defaultSnapPoint={0.5}
                snapPoints={[0.5, 0.9]}
                className="MobileInbox"
                onCloseTransitionStart={onCloseTransitionStart}
                dispatchUpdateActiveSnapPoint={updateActiveSnapPoint}
            >
                <Sheet>
                    <Header>
                        <Heading>Unsorted</Heading>
                        <CloseButton>Close</CloseButton>
                    </Header>

                    <PoiBoardSectionContext.Provider value={BoardSections.INBOX}>
                        <Content>
                            <MobileInboxContainerContent
                                {...props}
                                listContainerRef={listContainerRef}
                                cleanupOnOrchestrationEnd={cleanupOnOrchestrationEnd}
                            />
                        </Content>
                    </PoiBoardSectionContext.Provider>
                </Sheet>
            </SheetContainer>
        </ListPlaceholderContextProvider>
    );
};

export default MobileInboxContainer;
